/**
 * pages/BibliothequesV2.js
 * The Crowd v2 — Bibliotheques : liste et creation.
 *
 * Adaptation v2 de pages/Bibliotheques.js : memes appels API, meme
 * formulaire de creation (directeurs uniquement), memes droits. Un clic
 * sur une bibliotheque ouvre le visualiseur, sans changer d'URL (comme
 * aujourd'hui).
 */

import { useState, useEffect, useCallback } from "react";
import { getBibliotheques, createBibliotheque, getCollaborateurs } from "../services/api";
import BibliothequeViewerV2 from "./BibliothequeViewerV2";
import IconeV2 from "../components/IconeV2";

// Config JSON par defaut proposee a la creation (Socle Technique comme exemple)
const CONFIG_DEFAUT = `{
  "nom": "",
  "slug": "",
  "description": "",
  "versioning": true,
  "identifiant": "nom_fichier",
  "front_matter": [
    { "cle": "projet",     "label": "Projet",     "type": "text",     "bandeau": true  },
    { "cle": "statut",     "label": "Statut",     "type": "badge",    "bandeau": true  },
    { "cle": "avancement", "label": "Avancement", "type": "progress", "bandeau": true  },
    { "cle": "periode",    "label": "Periode",    "type": "text",     "bandeau": true  },
    { "cle": "date",       "label": "Date",       "type": "date",     "bandeau": true  }
  ],
  "dashboard": {
    "colonnes": ["projet", "statut", "avancement", "periode"],
    "filtres": ["statut"]
  }
}`;

const FORMULAIRE_VIDE = { slug: "", nom: "", description: "", config_json: CONFIG_DEFAUT, droits: [] };

function BadgeNiveau({ niveau }) {
  return (
    <span className={`v2-statut ${niveau === "full" ? "v2-statut-fort" : "v2-statut-neutre"}`}>
      {niveau === "full" ? "Full" : "Lecture"}
    </span>
  );
}

export default function BibliothequesV2({ collaborateur }) {
  const [bibliotheques, setBibliotheques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [selectedBibliotheque, setSelectedBibliotheque] = useState(null);
  const [form, setForm] = useState(FORMULAIRE_VIDE);

  const isDirecteur = collaborateur?.role === "directeur";

  const loadBibliotheques = useCallback(async () => {
    if (!collaborateur?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getBibliotheques(collaborateur.id);
      setBibliotheques(data);
    } catch (e) {
      setError("Erreur chargement des bibliothèques.");
    } finally {
      setLoading(false);
    }
  }, [collaborateur?.id]);

  useEffect(() => { loadBibliotheques(); }, [loadBibliotheques]);

  // Collaborateurs pour les droits (directeurs uniquement)
  useEffect(() => {
    if (!isDirecteur) return;
    getCollaborateurs().then(setCollaborateurs).catch(() => {});
  }, [isDirecteur]);

  function handleFormChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function handleDroitChange(collaborateurId, niveau) {
    setForm((f) => {
      const existing = f.droits.filter((d) => d.collaborateur_id !== collaborateurId);
      if (niveau === "") return { ...f, droits: existing };
      return { ...f, droits: [...existing, { collaborateur_id: collaborateurId, niveau }] };
    });
  }

  function getNiveauDroit(collaborateurId) {
    const d = form.droits.find((d) => d.collaborateur_id === collaborateurId);
    return d ? d.niveau : "";
  }

  async function handleSubmit() {
    if (!form.slug || !form.nom || !form.config_json) {
      setError("Slug, nom et config JSON sont obligatoires.");
      return;
    }
    try {
      JSON.parse(form.config_json);
    } catch (e) {
      setError(`Config JSON invalide : ${e.message}`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await createBibliotheque({ ...form, collaborateur_id: collaborateur.id });
      setSuccessMsg(`Bibliothèque « ${form.nom} » créée avec succès.`);
      setShowForm(false);
      setForm(FORMULAIRE_VIDE);
      await loadBibliotheques();
    } catch (e) {
      setError(e.message || "Erreur création bibliothèque.");
    } finally {
      setSaving(false);
    }
  }

  if (selectedBibliotheque) {
    return (
      <BibliothequeViewerV2
        bibliotheque={selectedBibliotheque}
        collaborateur={collaborateur}
        onBack={() => setSelectedBibliotheque(null)}
      />
    );
  }

  return (
    <div className="v2-page v2-page-module">
      <div className="v2-bib-entete">
        <header className="v2-module-entete">
          <h1 className="v2-module-titre">Bibliothèques</h1>
          <p className="v2-sous-titre">Bases documentaires : fiches, procédures et référentiel technique.</p>
        </header>
        {isDirecteur && (
          <button
            type="button"
            className="v2-btn v2-btn-secondaire"
            onClick={() => { setShowForm(!showForm); setError(null); setSuccessMsg(null); }}
          >
            {showForm ? "Annuler" : <><IconeV2 nom="plus" /> Nouvelle bibliothèque</>}
          </button>
        )}
      </div>

      {successMsg && <div className="v2-msg v2-msg-succes v2-marge-bas" role="status">{successMsg}</div>}
      {error && <div className="v2-msg v2-msg-erreur v2-marge-bas">{error}</div>}

      {/* ── Formulaire de creation ── */}
      {showForm && isDirecteur && (
        <section className="v2-panneau v2-bib-creation" aria-labelledby="v2-bib-creation-titre">
          <h2 id="v2-bib-creation-titre" className="v2-bloc-titre">Nouvelle bibliothèque</h2>

          <div className="v2-bib-creation-colonnes">
            <div className="v2-formulaire">
              <div className="v2-champs-ligne">
                <div className="v2-champ">
                  <label className="v2-label" htmlFor="v2-bib-nom">Nom <span className="v2-requis" aria-hidden="true">*</span></label>
                  <input id="v2-bib-nom" type="text" name="nom" value={form.nom} onChange={handleFormChange} placeholder="ex : Socle Technique" />
                </div>
                <div className="v2-champ">
                  <label className="v2-label" htmlFor="v2-bib-slug">Slug <span className="v2-requis" aria-hidden="true">*</span></label>
                  <input id="v2-bib-slug" type="text" name="slug" value={form.slug} onChange={handleFormChange} placeholder="ex : socle-technique" />
                  <span className="v2-champ-aide">Identifiant URL, sans espaces.</span>
                </div>
              </div>

              <div className="v2-champ">
                <label className="v2-label" htmlFor="v2-bib-description">Description</label>
                <input id="v2-bib-description" type="text" name="description" value={form.description} onChange={handleFormChange} placeholder="ex : Référentiel des projets et patterns IT Hopscotch" />
              </div>

              <div className="v2-champ">
                <label className="v2-label" htmlFor="v2-bib-config">Config JSON <span className="v2-requis" aria-hidden="true">*</span></label>
                <textarea id="v2-bib-config" name="config_json" className="v2-champ-code" value={form.config_json} onChange={handleFormChange} rows={18} spellCheck={false} />
                <span className="v2-champ-aide">Générée par Claude — coller ici.</span>
              </div>
            </div>

            {collaborateurs.length > 0 && (
              <div className="v2-bib-droits">
                <h3 className="v2-panneau-titre">Droits d'accès</h3>
                <p className="v2-champ-aide v2-marge-bas">Les directeurs ont toujours un accès complet. Définissez les droits des autres profils.</p>
                <div className="v2-tableau-conteneur v2-bib-droits-tableau">
                  <table className="v2-tableau">
                    <thead>
                      <tr>
                        <th scope="col">Collaborateur</th>
                        <th scope="col">Profil</th>
                        <th scope="col">Accès</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collaborateurs
                        .filter((c) => c.role !== "directeur")
                        .map((c) => (
                          <tr key={c.id}>
                            <td>{c.prenom} {c.nom}</td>
                            <td className="v2-cellule-discrete">{c.profil || c.role}</td>
                            <td>
                              <div className="v2-champ">
                                <label className="v2-invisible" htmlFor={`v2-droit-${c.id}`}>Accès de {c.prenom} {c.nom}</label>
                                <select id={`v2-droit-${c.id}`} value={getNiveauDroit(c.id)} onChange={(e) => handleDroitChange(c.id, e.target.value)}>
                                  <option value="">Aucun accès</option>
                                  <option value="lecture">Lecture</option>
                                  <option value="full">Full</option>
                                </select>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="v2-actions">
            <button type="button" className="v2-btn v2-btn-secondaire" onClick={() => setShowForm(false)}>Annuler</button>
            <button type="button" className="v2-btn v2-btn-principal" onClick={handleSubmit} disabled={saving}>
              {saving ? "Création…" : "Créer la bibliothèque"}
            </button>
          </div>
        </section>
      )}

      {/* ── Liste ── */}
      {loading ? (
        <div className="v2-chargement">Chargement…</div>
      ) : bibliotheques.length === 0 ? (
        <div className="v2-panneau">
          <p className="v2-vide">Aucune bibliothèque accessible.</p>
          {isDirecteur && <p className="v2-champ-aide">Créez votre première bibliothèque avec le bouton ci-dessus.</p>}
        </div>
      ) : (
        <div className="v2-grille v2-bib-grille">
          {bibliotheques.map((b) => (
            <button key={b.id} type="button" className="v2-carte v2-carte-normale" onClick={() => setSelectedBibliotheque(b)}>
              <span className="v2-carte-haut">
                <span className="v2-carte-icone" aria-hidden="true">
                  <IconeV2 nom="referentiel" taille={17} />
                </span>
                <span className="v2-carte-texte">
                  <span className="v2-carte-titre">
                    {b.nom}
                    {b.niveau && <BadgeNiveau niveau={b.niveau} />}
                    {isDirecteur && !b.niveau && <BadgeNiveau niveau="full" />}
                  </span>
                  {b.description && <span className="v2-carte-desc">{b.description}</span>}
                  <span className="v2-bib-slug">{b.slug}</span>
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
